//old import { useState } from "react";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
//old import { Loader2, Lock } from "lucide-react";
import { FileText, Loader2, Lock, Upload } from "lucide-react";
import api from "../../api/api";
//old import { Alert, Field, Panel, apiError, useOrgStyles, ORG_TYPE_LABEL } from "../../components/org/orgUi";
import { Alert, Field, Panel, apiError, downloadNonprofitProof, formatDate, useOrgStyles, ORG_TYPE_LABEL } from "../../components/org/orgUi";
import { useOrgPortal } from "./orgPortalContext";
import { licenseParts } from "./OrgLicenses";

const CONTACT_KEYS = [
    "primaryContactName", "primaryContactEmail", "primaryContactPhone",
    "secondaryContactName", "secondaryContactEmail", "secondaryContactPhone",
];

/**
 * Org admin settings: primary and secondary points of contact. The organization
 * name is fixed once the org exists; only Pono Pilot can change it.
 */
export default function OrgSettings() {
    //old const { orgId, org, reloadOrg, isPlatformAdmin } = useOrgPortal();
    const { orgId, org, reloadOrg, isPlatformAdmin, goTo } = useOrgPortal();
    const s = useOrgStyles();
    // what only Pono Pilot changes is asked for on the Requests page (Evan, Oct 16)
    const requestLink = (label) => (
        <button type="button" className={`${s.btnLink} !text-xs`} onClick={() => goTo("requests", { feature: "other" })}>{label}</button>
    );
    const [form, setForm] = useState({
        primaryContactName: org.PrimaryContactName || "",
        primaryContactEmail: org.PrimaryContactEmail || "",
        primaryContactPhone: org.PrimaryContactPhone || "",
        secondaryContactName: org.SecondaryContactName || "",
        secondaryContactEmail: org.SecondaryContactEmail || "",
        secondaryContactPhone: org.SecondaryContactPhone || "",
    });
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState({ kind: "", text: "" });
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    // a number of licenses the Licenses page's way: those on a paid plan are paid, the rest Pono Pilot
    // added outside a plan. The total, with how it's made up under it in small text like the Licenses page's
    // cards ("50" / "25 free + 25 paid"), so a narrow box never breaks the numbers apart
    const licenses = (base, purchased, paid) => (purchased > 0
        ? <>{base + purchased}<span className={`block text-xs ${s.muted}`}>{licenseParts(org.OrgType, base, purchased - (paid || 0), paid || 0)}</span></>
        : base);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put(`/org/${orgId}`, Object.fromEntries(CONTACT_KEYS.map((k) => [k, form[k]])));
            await reloadOrg();
            setMsg({ kind: "success", text: "Saved." });
        } catch (err) {
            setMsg({ kind: "error", text: apiError(err) });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="grid gap-6 lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-2">
                <Panel title="Organization">
                    <Field label="Organization name" htmlFor="os-name">
                        <div className="relative">
                            <input id="os-name" className={`${s.input} pr-10 opacity-75`} value={org.OrgName || ""} readOnly aria-readonly="true" />
                            <Lock size={16} className={`absolute right-3 top-1/2 -translate-y-1/2 ${s.muted}`} aria-hidden="true" />
                        </div>
                    </Field>
                    <p className={`mt-2 text-xs ${s.muted}`}>
                        {isPlatformAdmin
                            ? <>Rename organizations in <Link className={s.btnLink} to="/admin/organizations">Admin → Organizations</Link>.</>
                            //old : "Need to change your organization's name? Contact Pono Pilot."}
                            : <>Need to change your organization's name? {requestLink("Send us a request")}.</>}
                    </p>
                </Panel>
                <Panel title="Points of contact">
                    <Alert kind={msg.kind || "error"} onClose={() => setMsg({ kind: "", text: "" })}>{msg.text}</Alert>
                    <form onSubmit={save} className="space-y-6">
                        <ContactFields prefix="primary" title="Primary contact" form={form} set={set} />
                        {/* old <ContactFields prefix="secondary" title="Secondary contact (optional)" form={form} set={set} /> */}
                        {/* the secondary contact is the billing contact Alicia asked for (Oct 2): the emails about money go there too */}
                        <ContactFields prefix="secondary" title="Billing contact (optional)" form={form} set={set}
                                       hint="We also send payment and renewal emails here. They don't need an account." />
                        <div className="flex justify-end">
                            <button type="submit" className={s.btnPrimary} disabled={saving}>
                                {saving && <Loader2 size={14} className="animate-spin" />} Save changes
                            </button>
                        </div>
                    </form>
                </Panel>
                <FiscalYear orgId={orgId} org={org} reloadOrg={reloadOrg} />
                {/* old {org.OrgType === "nonprofit" && <NonprofitProof orgId={orgId} org={org} reloadOrg={reloadOrg} />} */}
                {/* only for one that said it's a 501(c)(3) at sign-up (ticked "later" or sent its proof): saying so is
                    optional since Alicia, Oct 30, and one that didn't sees nothing about it */}
                {org.OrgType === "nonprofit" && (org.Nonprofit501c3AttestedOn || org.Nonprofit501c3ProofFileName) && <NonprofitProof orgId={orgId} org={org} reloadOrg={reloadOrg} />}
            </div>
            <Panel title="Plan">
                <dl className={`space-y-3 text-sm ${s.body}`}>
                    <div className="flex justify-between"><dt>Type</dt><dd className={s.text}>{ORG_TYPE_LABEL[org.OrgType]}</dd></div>
                    {/* old <div className="flex justify-between"><dt>Coach licenses</dt><dd className={s.text}>{org.CoachLicenseLimit}</dd></div> */}
                    {/* "added" rather than "purchased": Pono Pilot can also set seats up for free */}
                    {/* old <div className="flex justify-between"><dt>Coach licenses</dt><dd className={s.text}>{org.CoachLicenseLimit}{org.CoachLicensesPurchased > 0 && ` + ${org.CoachLicensesPurchased} added`}</dd></div> */}
                    {/* (and the ones on a paid plan are paid, as the Licenses page and Admin → Organizations say: "added" is only
                        for the rest) */}
                    {/* (a number with its split can be longer than the room next to the label: then it goes whole onto a line of
                        its own under the label, right-aligned, instead of both breaking up and running together) */}
                    <div className="flex flex-wrap justify-between gap-x-4"><dt>Coach licenses</dt><dd className={`${s.text} ml-auto text-right`}>{licenses(org.CoachLicenseLimit, org.CoachLicensesPurchased, org.CoachLicensesPaid)}</dd></div>
                    {/* old <div className="flex justify-between"><dt>Student licenses</dt><dd className={s.text}>{org.StudentLicenseLimit}</dd></div> */}
                    {/* old <div className="flex justify-between"><dt>Student licenses</dt><dd className={s.text}>{org.StudentLicenseLimit}{org.StudentLicensesPurchased > 0 && ` + ${org.StudentLicensesPurchased} added`}</dd></div> */}
                    {/* old <div className="flex flex-wrap justify-between gap-x-4"><dt>Student licenses</dt><dd className={`${s.text} ml-auto text-right`}>{licenses(org.StudentLicenseLimit, org.StudentLicensesPurchased, org.StudentLicensesPaid)}</dd></div> */}
                    <div className="flex flex-wrap justify-between gap-x-4"><dt>Client licenses</dt><dd className={`${s.text} ml-auto text-right`}>{licenses(org.StudentLicenseLimit, org.StudentLicensesPurchased, org.StudentLicensesPaid)}</dd></div>
                    <div className="flex justify-between"><dt>Programs</dt><dd className={s.text}>{org.ProgramLimit}</dd></div>
                    {/* a Community Partner can have up to 3 org admins */}
                    {org.AdminLimit != null && <div className="flex justify-between"><dt>Org admins</dt><dd className={s.text}>{org.AdminCount} of {org.AdminLimit}</dd></div>}
                    {/* old <div className="flex justify-between"><dt>Student plan</dt><dd className={s.text}>{org.SponsoredStudentTierName || "None"}</dd></div> */}
                    {/* the name Admin → Organizations gives the same setting; a longer label, so the plan's name goes whole onto
                        its own line under it, right-aligned, when they don't fit side by side (as the licenses do above) */}
                    <div className="flex flex-wrap justify-between gap-x-4"><dt>Plan for licensed clients</dt><dd className={`${s.text} ml-auto text-right`}>{org.SponsoredStudentTierName || "None"}</dd></div>
                </dl>
                <p className={`mt-4 text-xs ${s.muted}`}>
                    {isPlatformAdmin
                        ? <>Change limits and plans in <Link className={s.btnLink} to="/admin/organizations">Admin → Organizations</Link>.</>
                        //old : "To change your plan or limits, contact Pono Pilot."}
                        // what Pono Pilot sets is asked for in a request; paying for it is the org's own, in
                        // Stripe (Alicia, Oct 2): one link for each, so this doesn't contradict the Licenses page
                        : <>To change any of these, {requestLink("send us a request")}. Price, renewal, card, invoices and canceling are under
                            Manage billing on the{" "}
                            <button type="button" className={`${s.btnLink} !text-xs`} onClick={() => goTo("licenses")}>Licenses</button> page.</>}
                </p>
            </Panel>
        </div>
    );
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October",
    "November", "December"];

/** The month the organization's fiscal year starts, for its impact report (Oct 16). */
function FiscalYear({ orgId, org, reloadOrg }) {
    const s = useOrgStyles();
    const [month, setMonth] = useState(org.FiscalYearStartMonth || 1);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState({ kind: "", text: "" });

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put(`/org/${orgId}`, { fiscalYearStartMonth: Number(month) });
            await reloadOrg();
            setMsg({ kind: "success", text: "Saved." });
        } catch (err) {
            setMsg({ kind: "error", text: apiError(err) });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Panel title="Fiscal year">
            <Alert kind={msg.kind || "error"} onClose={() => setMsg({ kind: "", text: "" })}>{msg.text}</Alert>
            <form onSubmit={save} className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <Field label="Your fiscal year starts in" htmlFor="os-fy" className="sm:w-64">
                    <select id="os-fy" className={s.input} value={month} onChange={(e) => setMonth(e.target.value)}>
                        {MONTHS.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
                    </select>
                </Field>
                <button type="submit" className={s.btnPrimary} disabled={saving}>
                    {saving && <Loader2 size={14} className="animate-spin" />} Save
                </button>
            </form>
            <p className={`mt-2 text-xs ${s.muted}`}>The impact report counts each fiscal year from the first day of this month.</p>
        </Panel>
    );
}

const PROOF_TYPES = [".pdf", ".png", ".jpg", ".jpeg"];
const PROOF_MAX_MB = 20;

/** A Community Partner's proof of 501(c)(3) status, uploaded after sign-up
 *  (Alicia, Oct 22). Pono Pilot sees it in Admin -> Organizations. */
function NonprofitProof({ orgId, org, reloadOrg }) {
    const s = useOrgStyles();
    const input = useRef(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState({ kind: "", text: "" });

    const upload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // picking the same file again still uploads it
        if (!file) return;
        const ext = file.name.includes(".") ? `.${file.name.split(".").pop().toLowerCase()}` : "";
        if (!PROOF_TYPES.includes(ext)) return setMsg({ kind: "error", text: "Only PDF, PNG, or JPG files are accepted" });
        if (file.size > PROOF_MAX_MB * 1024 * 1024) return setMsg({ kind: "error", text: `File exceeds ${PROOF_MAX_MB}MB limit` });
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            // unset the shared JSON Content-Type so the browser sends multipart (as Admin Resources does)
            await api.post(`/org/${orgId}/nonprofit-proof`, fd, { headers: { "Content-Type": undefined } });
            await reloadOrg();
            setMsg({ kind: "success", text: "Uploaded." });
        } catch (err) {
            setMsg({ kind: "error", text: apiError(err) });
        } finally {
            setBusy(false);
        }
    };

    const download = () => downloadNonprofitProof(orgId, org.Nonprofit501c3ProofFileName)
        .catch((err) => setMsg({ kind: "error", text: apiError(err) }));

    return (
        <Panel title="501(c)(3) proof">
            <Alert kind={msg.kind || "error"} onClose={() => setMsg({ kind: "", text: "" })}>{msg.text}</Alert>
            <p className={`text-sm ${s.body}`}>
                Upload your IRS determination letter or other proof that your organization is a 501(c)(3) nonprofit.
                PDF, PNG or JPG, up to {PROOF_MAX_MB} MB.
            </p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                {org.Nonprofit501c3ProofFileName ? (
                    <div className={`flex min-w-0 items-start gap-2 text-sm ${s.text}`}>
                        <FileText size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                        {/* a long file name wraps anywhere, so it can't push the page wider than a phone */}
                        <span className="min-w-0 [overflow-wrap:anywhere]">
                            {org.Nonprofit501c3ProofFileName}{" "}
                            <span className={s.muted}>· {formatDate(org.Nonprofit501c3ProofUploadedOn)}</span>{" "}
                            <button type="button" className={s.btnLink} onClick={download}>Download</button>
                        </span>
                    </div>
                ) : (
                    <p className={`text-sm ${s.muted}`}>Not uploaded yet.</p>
                )}
                <input ref={input} type="file" accept={PROOF_TYPES.join(",")} className="sr-only" onChange={upload} tabIndex={-1} aria-hidden="true" />
                <button type="button" className={s.btnSecondary} disabled={busy} onClick={() => input.current?.click()}>
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {org.Nonprofit501c3ProofFileName ? "Replace" : "Upload proof"}
                </button>
            </div>
        </Panel>
    );
}

//old function ContactFields({ prefix, title, form, set }) {
function ContactFields({ prefix, title, hint, form, set }) {
    const s = useOrgStyles();
    return (
        <fieldset>
            <legend className={`mb-3 text-sm font-semibold ${s.text}`}>{title}</legend>
            {hint && <p className={`-mt-2 mb-3 text-xs ${s.muted}`}>{hint}</p>}
            <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Name" htmlFor={`os-${prefix}-name`} className="sm:col-span-2">
                    <input id={`os-${prefix}-name`} className={s.input} value={form[`${prefix}ContactName`]} onChange={set(`${prefix}ContactName`)} maxLength={200} />
                </Field>
                <Field label="Email" htmlFor={`os-${prefix}-email`}>
                    <input id={`os-${prefix}-email`} type="email" className={s.input} value={form[`${prefix}ContactEmail`]} onChange={set(`${prefix}ContactEmail`)} maxLength={255} />
                </Field>
                <Field label="Phone" htmlFor={`os-${prefix}-phone`}>
                    <input id={`os-${prefix}-phone`} type="tel" className={s.input} value={form[`${prefix}ContactPhone`]} onChange={set(`${prefix}ContactPhone`)} maxLength={50} />
                </Field>
            </div>
        </fieldset>
    );
}
